import { createContext, useContext, useEffect, useState } from 'react';
import { AuthState, LoginCredentials, LoginResponse } from '@/types/auth';
import { login as authLogin, getStoredAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => void;
  isTokenValid: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token validity duration - 24 hours in milliseconds
const TOKEN_VALIDITY_DURATION = 24 * 60 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null
  });
  const navigate = useNavigate();

  // Helper function to check token validity
  const isTokenValid = (): boolean => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      if (!auth.token) return false;
      
      // Check expiry if available
      if (auth.expiresAt) {
        return new Date().getTime() < auth.expiresAt;
      }
      
      // Legacy tokens without expiry, assume valid
      // (this will be replaced after first login with the new system)
      return true;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const stored = getStoredAuth();
    if (stored && isTokenValid()) {
      setState({
        user: stored.user,
        isLoading: false,
        error: null
      });
    } else {
      if (stored && !isTokenValid()) {
        // Clear invalid token
        localStorage.removeItem('auth');
      }
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const handleLogin = async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await authLogin(credentials);
      
      // Store token with expiry information
      const expiresAt = new Date().getTime() + TOKEN_VALIDITY_DURATION;
      
      // Store both token and user data with expiry
      const authData = {
        token: response.token,
        user: response.user,
        expiresAt: expiresAt
      };
      localStorage.setItem('auth', JSON.stringify(authData));
      
      setState({
        user: response.user,
        isLoading: false,
        error: null
      });
  
      return response;
    } catch (error) {
      console.error('AuthContext: Login failed', error);
      setState({
        user: null,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      });
      throw error;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth');
    setState({
      user: null,
      isLoading: false,
      error: null
    });
    navigate('/');
  };

  const value = {
    ...state,
    login: handleLogin,
    logout: handleLogout,
    isTokenValid
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}